import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Heading,
  Icon,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { getTenantId, TenantId, tenants } from '@pelicargo/tenants';
import { Show, useError, useSuccess } from '@pelicargo/ui';
import { toCents, toDollars, TRUCKING_PROVIDERS } from '@pelicargo/utils';
import { AccountType, FreightService, LineItemType, MarkupCategory, MarkupConfigurationType } from '@prisma/client';
import { useEffect, useState } from 'react';
import { FieldErrors, useForm } from 'react-hook-form';
import { HiArrowsRightLeft } from 'react-icons/hi2';
import { z } from 'zod';

import { LineItemAudience } from '../../../../api/src/lib/kysley/enums';
import { trpc, TrpcInputs, TrpcOutputs } from '../../config/trpc';
import { useAuth } from '../../context/AuthProvider';
import { requiredNumberSchema } from '../../utils/schemas';
import { AlertModal } from '../AlertModal/AlertModal';
import { BaseDrawer } from '../BaseDrawer';
import { ClassicMarkupConfigurationForm } from '../Forms/ClassicMarkupConfigurationForm';
import { MarkupConfigurationForm } from '../Forms/MarkupConfigurationForm';
import { SimpleForm } from '../SimpleForm';

// Money is edited in dollars and stored in cents (see `toFormValues` / `onValid`)
const costSchema = z.object({
  id: z.coerce.number().optional(),
  min: requiredNumberSchema,
  cost: requiredNumberSchema,
});

const markupSchema = z
  .object({
    id: z.coerce.number().optional(),
    label: z.string().min(1, 'Required'),
    category: z.nativeEnum(MarkupCategory),
    min_cost: z.coerce.number(),
    line_item_types: z.array(z.nativeEnum(LineItemType)).default([]),
    // Rows are removed with `useFieldArray`'s `remove`, which splices, so there should never
    // be a gap. `nullish` keeps a stray hole (or an explicit null) from failing the whole
    // save silently — the server filters them out either way.
    costs: z.array(costSchema.nullish()).default([]),
    applicable_freight_services: z.array(z.nativeEnum(FreightService)).min(1, 'Pick at least one freight service'),
    child_itemized_for: z.array(z.nativeEnum(LineItemAudience)).optional(),
    child_excluded_for: z.array(z.nativeEnum(LineItemAudience)).optional(),
  })
  // Each tier ends where the next begins, so two tiers starting at the same weight can't both apply
  .superRefine((markup, ctx) => {
    const mins = markup.costs.filter(Boolean).map((cost) => cost?.min);
    mins.forEach((min, index) => {
      if (mins.indexOf(min) === index) return;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['costs', index, 'min'],
        message: 'Another tier already starts at this weight',
      });
    });
  });

const schema = z
  .object({
    id: z.coerce.number().optional(),
    label: z.string().min(1, 'Required'),
    type: z.nativeEnum(MarkupConfigurationType),
    tenant_id: z.enum(Object.keys(tenants) as [TenantId, ...TenantId[]]),
    // The select hands back a string; empty means a System/Tenant configuration with no owner
    account_as_fulfillment_partner_id: z.preprocess(
      (value) => (value === '' || value === undefined || value === null ? null : Number(value)),
      z.number().nullable(),
    ),
    providers: z.array(z.string()).default([]),
    origin_airports: z.array(z.string()).default([]),
    account_as_requestors: z.array(z.coerce.number()).default([]),
    account_types: z.array(z.nativeEnum(AccountType)).default([]),
    trucking_providers: z.array(z.enum(TRUCKING_PROVIDERS)).default([]),
    is_restricted: z.boolean().default(false),
    markups: z.array(markupSchema.nullish()).default([]),
  })
  // Mirrors the server check. A FULFILLMENT_PARTNER configuration with no partner matches
  // as if it were a System/Tenant one, which is how a null ends up poisoning markup matching.
  .superRefine((values, ctx) => {
    if (
      values.type === MarkupConfigurationType.FULFILLMENT_PARTNER &&
      values.account_as_fulfillment_partner_id == null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['account_as_fulfillment_partner_id'],
        message: 'Required for Fulfillment Partner configurations',
      });
    }
  });

type FormValues = z.infer<typeof schema>;
type UpsertInput = Exclude<TrpcInputs['upsertFPC'], void>;

type MarkupConfiguration = NonNullable<TrpcOutputs['getMarkupConfiguration']>;

// Opened from an account, it's that account's FP configuration; otherwise a Tenant one (PEL-3045)
const emptyValues = (fulfillmentPartnerId?: number): Partial<FormValues> => ({
  id: undefined,
  label: '',
  type: fulfillmentPartnerId ? MarkupConfigurationType.FULFILLMENT_PARTNER : MarkupConfigurationType.TENANT,
  tenant_id: getTenantId(),
  account_as_fulfillment_partner_id: fulfillmentPartnerId ?? null,
  providers: [],
  origin_airports: [],
  account_as_requestors: [],
  account_types: [],
  trucking_providers: [],
  is_restricted: false,
  markups: [],
});

const toFormValues = (data: MarkupConfiguration) =>
  ({
    id: data.id,
    label: data.label,
    type: data.type,
    tenant_id: data.tenant_id,
    account_as_fulfillment_partner_id: data.account_as_fulfillment_partner_id ?? null,
    providers: data.providers.map(({ id }) => id),
    origin_airports: data.origin_airports.map(({ id }) => id),
    account_as_requestors: data.account_as_requestors.map(({ id }) => id),
    account_types: data.account_types,
    trucking_providers: data.trucking_providers,
    is_restricted: data.is_restricted,
    markups: data.markups.map((markup) => ({
      id: markup.id,
      label: markup.label,
      category: markup.category,
      min_cost: toDollars(markup.min_cost),
      line_item_types: markup.line_item_types,
      applicable_freight_services: markup.applicable_freight_services,
      costs: markup.costs.map((cost) => ({ id: cost.id, min: cost.min, cost: toDollars(cost.cost) })),
      child_itemized_for: markup.child_itemized_for,
      child_excluded_for: markup.child_excluded_for,
    })),
  }) as FormValues;

const toCentsMarkups = (markups: FormValues['markups']) =>
  markups.map((markup) =>
    markup
      ? {
          ...markup,
          min_cost: toCents(markup.min_cost),
          costs: markup.costs.map((cost) => (cost ? { ...cost, cost: toCents(cost.cost) } : cost)),
        }
      : markup,
  );

type Props = {
  id?: number | null;
  isOpen: boolean;
  onClose: () => void;
  /** Pins the configuration to an account when opened from the account drawer */
  defaultFulfillmentPartnerId?: number;
};

export const MarkupConfigurationDrawer = ({ id, isOpen, onClose, defaultFulfillmentPartnerId }: Props) => {
  const { handleSuccess } = useSuccess();
  const { handleError } = useError();
  const { isSuperAdminOrSystemTenant } = useAuth();
  const toast = useToast();
  const deleteConfirmation = useDisclosure();
  const utils = trpc.useUtils();

  const methods = useForm<FormValues>({ resolver: zodResolver(schema) });

  const { data } = trpc.getMarkupConfiguration.useQuery({ id: id as number }, { enabled: !!id });

  const { mutateAsync: upsertMarkupConfiguration, isLoading: isSaving } = trpc.upsertFPC.useMutation();
  const { mutateAsync: deleteMarkupConfiguration, isLoading: isDeleting } =
    trpc.deleteMarkupConfiguration.useMutation();

  useEffect(() => {
    if (!id) {
      methods.reset(emptyValues(defaultFulfillmentPartnerId) as FormValues);
      return;
    }
    if (!data) return;

    methods.reset(toFormValues(data));
  }, [data, id, defaultFulfillmentPartnerId, methods]);

  // Everyone gets the new form; Peli's own admins can fall back to the classic one (PEL-3045)
  const [isClassicMode, setIsClassicMode] = useState(false);
  const showClassicForm = isClassicMode;
  const showModeToggle = isSuperAdminOrSystemTenant;
  const showDelete = !!id;
  const showId = !!id && showClassicForm;
  const isSubmitDisabled = !showClassicForm && !methods.formState.isValid;

  const title = id ? 'Edit Markup Configuration' : 'New Markup Configuration';
  const submitCopy = id ? 'Save Configuration' : 'Create Configuration';
  const modeToggleCopy = isClassicMode ? 'Switch to new form' : 'Switch to classic form';

  const invalidate = async () => {
    await Promise.all([
      utils.getMarkupConfigurations.invalidate(),
      utils.getMarkupConfigurationsByAccount.invalidate(),
      id ? utils.getMarkupConfiguration.invalidate({ id }) : Promise.resolve(),
    ]);
  };

  const onValid = async (values: FormValues) => {
    try {
      await upsertMarkupConfiguration({
        ...values,
        markups: toCentsMarkups(values.markups),
        // The server types this with the trucking `Provider` string enum, which a plain
        // string-literal union isn't assignable to. Values are identical.
        trucking_providers: values.trucking_providers as UpsertInput['trucking_providers'],
      });
      await invalidate();
      handleSuccess(id ? 'Markup configuration updated' : 'Markup configuration created');
      onClose();
    } catch (error) {
      handleError(error);
    }
  };

  // Some fields — a removed tiered cost, a collapsed markup — have no visible control to hang
  // an error off, so without this the submit button would just do nothing.
  const onInvalid = (errors: FieldErrors<FormValues>) => {
    toast({
      title: 'This configuration has some errors',
      description: `Check: ${Object.keys(errors).join(', ')}`,
      status: 'error',
      duration: 6000,
      isClosable: true,
    });
  };

  const handleDelete = async () => {
    try {
      await deleteMarkupConfiguration({ id: id as number });
      await invalidate();
      deleteConfirmation.onClose();
      handleSuccess('Markup configuration deleted');
      onClose();
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <>
      <BaseDrawer isOpen={isOpen} onClose={onClose}>
        <SimpleForm {...methods} submitOnBlur={false}>
          <form
            onSubmit={methods.handleSubmit(onValid, onInvalid)}
            style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
          >
            <DrawerHeader borderBottomWidth="1px" pr="14">
              <Stack spacing="1" align="flex-start">
                <Heading>{title}</Heading>
                <Show if={showModeToggle}>
                  <Button
                    variant="link"
                    size="sm"
                    color="fg.muted"
                    fontWeight="medium"
                    leftIcon={<Icon as={HiArrowsRightLeft} />}
                    onClick={() => setIsClassicMode((isClassic) => !isClassic)}
                  >
                    {modeToggleCopy}
                  </Button>
                </Show>
              </Stack>
            </DrawerHeader>
            <DrawerBody py="5">
              <Show if={!showClassicForm}>
                <MarkupConfigurationForm lockedFulfillmentPartnerId={defaultFulfillmentPartnerId} />
              </Show>
              <Show if={showClassicForm}>
                <ClassicMarkupConfigurationForm lockedFulfillmentPartnerId={defaultFulfillmentPartnerId} />
              </Show>
            </DrawerBody>
            <DrawerFooter borderTopWidth="1px">
              <Stack direction="row" justify="space-between" align="center" w="full" spacing="2">
                <Show if={showId}>
                  <Text>ID: {id}</Text>
                </Show>
                <Show if={showDelete}>
                  <Button colorScheme="red" variant="outline" onClick={deleteConfirmation.onOpen}>
                    Delete
                  </Button>
                </Show>
                <Stack direction="row" spacing="2" ml="auto">
                  <Button variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" colorScheme="brand" isLoading={isSaving} isDisabled={isSubmitDisabled}>
                    {submitCopy}
                  </Button>
                </Stack>
              </Stack>
            </DrawerFooter>
          </form>
        </SimpleForm>
      </BaseDrawer>
      <AlertModal
        isOpen={deleteConfirmation.isOpen}
        onClose={deleteConfirmation.onClose}
        title="Delete markup configuration?"
        message="This also deletes its markups and tiered costs. This cannot be undone."
        btnCancel="Cancel"
        btnDelete="Delete"
        isDeleting={isDeleting}
        handleConfirm={handleDelete}
      />
    </>
  );
};
